import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Award, Upload, Users, FileSpreadsheet, Send, Eye, 
  ChevronDown, ChevronUp, X, Check, Plus, ArrowLeft, Trash2, Edit, Save
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component for creating a new Competition Analysis
const CreateCompetitionAnalysis = ({ onClose, onCreated }) => {
  const [events, setEvents] = useState([]);
  const [judges, setJudges] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedJudges, setSelectedJudges] = useState([]);
  const [file, setFile] = useState(null);
  const [previewAthletes, setPreviewAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchJudges();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/speaker-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Errore nel caricamento degli eventi');
    }
  };

  const fetchJudges = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/judges-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJudges(response.data);
    } catch (error) {
      console.error('Error fetching judges:', error);
      toast.error('Errore nel caricamento dei giudici');
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setLoadingPreview(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/competition-analysis/upload-athletes`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setPreviewAthletes(response.data.athletes);
      toast.success(`${response.data.count} atleti caricati`);
    } catch (error) {
      console.error('Error previewing file:', error);
      toast.error(error.response?.data?.detail || 'Errore nel caricamento del file');
      setFile(null);
      setPreviewAthletes([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleJudge = (judgeId) => {
    setSelectedJudges(prev => 
      prev.includes(judgeId) 
        ? prev.filter(id => id !== judgeId)
        : [...prev, judgeId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedEvent) {
      toast.error('Seleziona un evento');
      return;
    }
    if (selectedJudges.length === 0) {
      toast.error('Seleziona almeno un giudice');
      return;
    }
    if (!file) {
      toast.error('Carica il file Excel con gli atleti');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('event_id', selectedEvent);
      formData.append('judge_ids', selectedJudges.join(','));
      formData.append('file', file);

      await axios.post(`${API}/competition-analysis`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Analysis di Gara creata con successo');
      onCreated();
      onClose();
    } catch (error) {
      console.error('Error creating analysis:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Crea Analysis di Gara</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Step 1: Select Event */}
          <div className="space-y-2">
            <Label className="text-lg font-semibold">1. Seleziona Evento</Label>
            <Select value={selectedEvent} onValueChange={setSelectedEvent}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Seleziona un evento" />
              </SelectTrigger>
              <SelectContent>
                {events.map((event) => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.event_code ? `[${event.event_code}] ` : ''}{event.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Step 2: Upload Excel */}
          <div className="space-y-2">
            <Label className="text-lg font-semibold">2. Carica File Excel Atleti</Label>
            <p className="text-sm text-gray-500">
              Il file deve avere 4 colonne: Nome, Cognome, Numero di Competizione, Evento
            </p>
            <div className="flex items-center space-x-4">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="flex-1"
                data-testid="excel-file-input"
              />
              {loadingPreview && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
              )}
            </div>
            
            {previewAthletes.length > 0 && (
              <div className="mt-4 border rounded-lg p-4 bg-gray-50 max-h-48 overflow-y-auto">
                <p className="font-medium mb-2">Anteprima: {previewAthletes.length} atleti</p>
                <table className="w-full text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="p-2 text-left font-bold">N° Gara</th>
                      <th className="p-2 text-left font-bold">Nome</th>
                      <th className="p-2 text-left font-bold">Cognome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewAthletes.slice(0, 10).map((athlete, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{athlete.competition_number}</td>
                        <td className="p-2">{athlete.name}</td>
                        <td className="p-2">{athlete.surname}</td>
                      </tr>
                    ))}
                    {previewAthletes.length > 10 && (
                      <tr className="border-t bg-gray-100">
                        <td colSpan={3} className="p-2 text-center text-gray-500">
                          ... e altri {previewAthletes.length - 10} atleti
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Step 3: Select Judges */}
          <div className="space-y-2">
            <Label className="text-lg font-semibold">3. Seleziona Giudici</Label>
            <p className="text-sm text-gray-500">
              Seleziona i giudici che valuteranno gli atleti
            </p>
            <div className="border rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
              {judges.map((judge) => (
                <div
                  key={judge.id}
                  onClick={() => toggleJudge(judge.id)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center justify-between ${
                    selectedJudges.includes(judge.id)
                      ? 'bg-green-100 border-2 border-green-500'
                      : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <div>
                    <p className="font-medium">{judge.name} {judge.surname}</p>
                    <p className="text-sm text-gray-500">{judge.email}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">
                      {(judge.roles || [judge.role]).filter(r => r !== 'atleta').join(', ')}
                    </Badge>
                    {selectedJudges.includes(judge.id) && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            {selectedJudges.length > 0 && (
              <p className="text-sm text-green-600">
                {selectedJudges.length} giudice/i selezionato/i
              </p>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !selectedEvent || selectedJudges.length === 0 || !file}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? 'Creazione...' : 'Crea Analysis di Gara'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Component for judge to submit scores
const JudgeScoreForm = ({ analysis, onScoreSubmitted, judgedAthletes = [] }) => {
  const [competitionNumber, setCompetitionNumber] = useState('');
  const [tecnica, setTecnica] = useState('');
  const [coreografia, setCoreografia] = useState('');
  const [immagine, setImmagine] = useState('');
  const [loading, setLoading] = useState(false);

  const total = (parseInt(tecnica) || 0) + (parseInt(coreografia) || 0) + (parseInt(immagine) || 0);
  const isValid = competitionNumber && tecnica >= 1 && tecnica <= 10 && 
                  coreografia >= 1 && coreografia <= 10 && 
                  immagine >= 1 && immagine <= 10;
  
  const isUpdating = judgedAthletes.includes(competitionNumber);

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error('Compila tutti i campi correttamente (voti da 1 a 10)');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/competition-analysis/${analysis.id}/judgment`, {
        analysis_id: analysis.id,
        competition_number: competitionNumber,
        tecnica: parseInt(tecnica),
        coreografia: parseInt(coreografia),
        immagine: parseInt(immagine)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(isUpdating ? 'Giudizio aggiornato con successo' : 'Giudizio inviato con successo');
      setCompetitionNumber('');
      setTecnica('');
      setCoreografia('');
      setImmagine('');
      onScoreSubmitted();
    } catch (error) {
      console.error('Error submitting judgment:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'invio del giudizio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader className="bg-gradient-to-r from-red-50 to-red-100">
        <CardTitle className="text-lg">Metodo di Giudizio 3D (tridimensionale)</CardTitle>
        <CardDescription>
          Per ogni parametro di giudizio voto minimo 1 - voto massimo 10 (senza decimali)
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {isUpdating && (
          <div className="mb-4 p-2 bg-orange-100 border border-orange-300 rounded-lg text-center text-orange-800 text-sm">
            ⚠️ Atleta già giudicato - il nuovo voto sovrascriverà il precedente
          </div>
        )}
        <div className="grid grid-cols-6 gap-2 mb-4">
          <div className="text-center">
            <Label className="font-bold text-sm">N° GARA</Label>
          </div>
          <div className="text-center">
            <Label className="font-bold text-sm">TECNICA</Label>
          </div>
          <div className="text-center">
            <Label className="font-bold text-sm">COREOGRAFIA</Label>
          </div>
          <div className="text-center">
            <Label className="font-bold text-sm">IMMAGINE</Label>
          </div>
          <div className="text-center">
            <Label className="font-bold text-sm">TOTALE</Label>
          </div>
          <div className="text-center">
            <Label className="font-bold text-sm">AZIONE</Label>
          </div>
        </div>
        
        <div className="grid grid-cols-6 gap-2 items-center">
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={competitionNumber}
            onChange={(e) => setCompetitionNumber(e.target.value)}
            placeholder="N°"
            className={`text-center ${isUpdating ? 'border-orange-400 bg-orange-50' : ''}`}
            data-testid="competition-number-input"
          />
          <Input
            type="number"
            inputMode="numeric"
            min="1"
            max="10"
            value={tecnica}
            onChange={(e) => setTecnica(e.target.value)}
            placeholder="1-10"
            className="text-center"
            data-testid="tecnica-input"
          />
          <Input
            type="number"
            inputMode="numeric"
            min="1"
            max="10"
            value={coreografia}
            onChange={(e) => setCoreografia(e.target.value)}
            placeholder="1-10"
            className="text-center"
            data-testid="coreografia-input"
          />
          <Input
            type="number"
            inputMode="numeric"
            min="1"
            max="10"
            value={immagine}
            onChange={(e) => setImmagine(e.target.value)}
            placeholder="1-10"
            className="text-center"
            data-testid="immagine-input"
          />
          <div className="text-center py-2 bg-gray-100 rounded font-bold text-lg">
            {total > 0 ? total : '-'}
          </div>
          <Button
            onClick={handleSubmit}
            disabled={loading || !isValid}
            className="bg-green-600 hover:bg-green-700"
            data-testid="submit-judgment-btn"
          >
            {loading ? '...' : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Component to view judgments grouped by athlete
const JudgmentsByAthlete = ({ analysisId, analysisStatus, onPublish }) => {
  const [groupedJudgments, setGroupedJudgments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAthletes, setExpandedAthletes] = useState({});
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    fetchJudgments();
  }, [analysisId]);

  const fetchJudgments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/competition-analysis/${analysisId}/judgments-by-athlete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupedJudgments(response.data);
    } catch (error) {
      console.error('Error fetching judgments:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAthlete = (competitionNumber) => {
    setExpandedAthletes(prev => ({
      ...prev,
      [competitionNumber]: !prev[competitionNumber]
    }));
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/competition-analysis/${analysisId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Pubblicati ${response.data.published_to_athletes} giudizi agli atleti`);
      
      if (response.data.athletes_not_found?.length > 0) {
        toast.warning(`Atleti non trovati nel sistema: ${response.data.athletes_not_found.join(', ')}`);
      }
      
      setShowPublishDialog(false);
      if (onPublish) onPublish();
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error(error.response?.data?.detail || 'Errore nella pubblicazione');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Caricamento giudizi...</div>;
  }

  if (groupedJudgments.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Nessun giudizio ancora registrato
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="font-bold text-lg">Giudizi per Atleta</h3>
      {groupedJudgments.map((group) => (
        <Card key={group.competition_number} className="overflow-hidden">
          <div
            onClick={() => toggleAthlete(group.competition_number)}
            className="p-4 bg-gray-50 cursor-pointer flex items-center justify-between hover:bg-gray-100"
          >
            <div className="flex items-center space-x-4">
              <Badge variant="secondary" className="text-lg font-bold">
                N° {group.competition_number}
              </Badge>
              <span className="font-semibold">
                {group.athlete_name} {group.athlete_surname}
              </span>
              <Badge>{group.judgments.length} giudizi</Badge>
            </div>
            {expandedAthletes[group.competition_number] ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
          
          {expandedAthletes[group.competition_number] && (
            <CardContent className="pt-4">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left font-bold">Giudice</th>
                    <th className="p-2 text-center font-bold">Tecnica</th>
                    <th className="p-2 text-center font-bold">Coreografia</th>
                    <th className="p-2 text-center font-bold">Immagine</th>
                    <th className="p-2 text-center font-bold">Totale</th>
                    <th className="p-2 text-right font-bold">Data/Ora</th>
                  </tr>
                </thead>
                <tbody>
                  {group.judgments.map((j, idx) => (
                    <tr key={j.id || idx} className="border-t">
                      <td className="p-2">{j.judge_name}</td>
                      <td className="p-2 text-center">{j.tecnica}</td>
                      <td className="p-2 text-center">{j.coreografia}</td>
                      <td className="p-2 text-center">{j.immagine}</td>
                      <td className="p-2 text-center font-bold">{j.total}</td>
                      <td className="p-2 text-right text-sm text-gray-500">
                        {new Date(j.created_at).toLocaleString('it-IT')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          )}
        </Card>
      ))}
      
      {/* Publish Button */}
      {analysisStatus !== 'published' && groupedJudgments.length > 0 && (
        <div className="mt-8 pt-6 border-t">
          <Button
            onClick={() => setShowPublishDialog(true)}
            className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
            data-testid="publish-btn"
          >
            <Check className="w-5 h-5 mr-2" />
            PUBBLICA
          </Button>
        </div>
      )}
      
      {analysisStatus === 'published' && (
        <div className="mt-8 pt-6 border-t">
          <div className="bg-green-100 border border-green-300 rounded-lg p-4 text-center">
            <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-green-800 font-semibold">Giudizi già pubblicati</p>
            <p className="text-green-600 text-sm">I risultati sono stati inviati alle dashboard degli atleti</p>
          </div>
        </div>
      )}
      
      {/* Publish Confirmation Dialog */}
      {showPublishDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4 text-center">Conferma Pubblicazione</h3>
            <p className="text-gray-600 mb-6 text-center">
              CONFERMI di voler pubblicare i giudizi nelle Dashboard degli Atleti e assegnare loro i Crediti Tecnici di riferimento?
            </p>
            <div className="flex space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowPublishDialog(false)}
                className="flex-1"
                disabled={publishing}
              >
                Annulla
              </Button>
              <Button
                onClick={handlePublish}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={publishing}
              >
                {publishing ? 'Pubblicazione...' : 'Conferma'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Component to show athlete's own competition judgments
const AthleteCompetitionJudgments = () => {
  const [judgments, setJudgments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJudgments, setExpandedJudgments] = useState({});

  useEffect(() => {
    fetchMyJudgments();
  }, []);

  const fetchMyJudgments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/my-competition-judgments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJudgments(response.data);
    } catch (error) {
      console.error('Error fetching judgments:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleJudgment = (id) => {
    setExpandedJudgments(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (judgments.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Nessun giudizio di gara pubblicato</p>
        <p className="text-sm mt-1">I risultati delle tue gare appariranno qui</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {judgments.map((judgment) => (
        <Card key={judgment.id} className="overflow-hidden">
          <div
            onClick={() => toggleJudgment(judgment.id)}
            className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 cursor-pointer flex items-center justify-between hover:from-blue-100 hover:to-blue-150"
          >
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                {judgment.event_code && (
                  <Badge variant="outline" className="font-mono">{judgment.event_code}</Badge>
                )}
                <h3 className="font-semibold">{judgment.event_title}</h3>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span>N° Gara: <strong>{judgment.competition_number}</strong></span>
                <span>Media: <strong>{Math.round(judgment.average_total)}/30</strong></span>
                <Badge className="bg-purple-500">+{judgment.technical_credits_assigned} Crediti Tecnici</Badge>
              </div>
            </div>
            {expandedJudgments[judgment.id] ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
          
          {expandedJudgments[judgment.id] && (
            <CardContent className="pt-4">
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-700">Metodo di Giudizio 3D (tridimensionale)</p>
                <p className="text-sm text-gray-500">Per ogni parametro: voto minimo 1 - voto massimo 10</p>
              </div>
              
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left font-bold">Giudice</th>
                    <th className="p-2 text-center font-bold">Tecnica</th>
                    <th className="p-2 text-center font-bold">Coreografia</th>
                    <th className="p-2 text-center font-bold">Immagine</th>
                    <th className="p-2 text-center font-bold">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {judgment.judgments.map((j, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-2">{j.judge_name}</td>
                      <td className="p-2 text-center">{j.tecnica}</td>
                      <td className="p-2 text-center">{j.coreografia}</td>
                      <td className="p-2 text-center">{j.immagine}</td>
                      <td className="p-2 text-center font-bold">{j.total}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-blue-50">
                  <tr>
                    <td className="p-2 font-bold">MEDIA</td>
                    <td colSpan={3}></td>
                    <td className="p-2 text-center font-bold text-lg text-blue-700">
                      {Math.round(judgment.average_total)}/30
                    </td>
                  </tr>
                  <tr className="bg-purple-50">
                    <td className="p-2 font-bold text-purple-700">CREDITI TECNICI</td>
                    <td colSpan={3}></td>
                    <td className="p-2 text-center font-bold text-lg text-purple-700">
                      +{judgment.technical_credits_assigned}
                    </td>
                  </tr>
                </tfoot>
              </table>
              
              <div className="mt-4 text-sm text-gray-500 text-right">
                Pubblicato il {new Date(judgment.published_at).toLocaleDateString('it-IT')} da {judgment.published_by_name}
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
};

// Judge View Component - handles scoring with athlete highlighting
const JudgeView = ({ analysis, onBack, onScoreSubmitted }) => {
  const [judgedAthletes, setJudgedAthletes] = useState([]);
  const [myJudgments, setMyJudgments] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyJudgments();
  }, [analysis.id]);

  const fetchMyJudgments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/competition-analysis/${analysis.id}/my-judgments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJudgedAthletes(response.data.judged_athletes || []);
      
      // Create a lookup object for judgments by competition number
      const judgmentsMap = {};
      (response.data.judgments || []).forEach(j => {
        judgmentsMap[j.competition_number] = j;
      });
      setMyJudgments(judgmentsMap);
    } catch (error) {
      console.error('Error fetching my judgments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreSubmitted = () => {
    fetchMyJudgments();
    if (onScoreSubmitted) onScoreSubmitted();
  };

  const isAthleteJudged = (competitionNumber) => {
    return judgedAthletes.includes(competitionNumber);
  };

  const getJudgmentForAthlete = (competitionNumber) => {
    return myJudgments[competitionNumber];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Indietro
        </Button>
        <h2 className="text-xl font-bold">
          Giudica: {analysis.event_title}
        </h2>
        {analysis.event_code && (
          <Badge variant="secondary">{analysis.event_code}</Badge>
        )}
      </div>

      <JudgeScoreForm 
        analysis={analysis} 
        onScoreSubmitted={handleScoreSubmitted}
        judgedAthletes={judgedAthletes}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Atleti in Gara ({analysis.athletes?.length || 0})</span>
            <span className="text-sm font-normal text-gray-500">
              Giudicati: {judgedAthletes.length}/{analysis.athletes?.length || 0}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">Caricamento...</div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="p-2 text-left font-bold">N° Gara</th>
                    <th className="p-2 text-left font-bold">Nome</th>
                    <th className="p-2 text-left font-bold">Cognome</th>
                    <th className="p-2 text-center font-bold">Stato</th>
                    <th className="p-2 text-center font-bold">Voti</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.athletes?.map((athlete, idx) => {
                    const judged = isAthleteJudged(athlete.competition_number);
                    const judgment = getJudgmentForAthlete(athlete.competition_number);
                    return (
                      <tr 
                        key={idx} 
                        className={`border-t ${judged ? 'bg-orange-100' : 'bg-white hover:bg-gray-50'}`}
                      >
                        <td className="p-2 font-mono font-bold">{athlete.competition_number}</td>
                        <td className="p-2">{athlete.name}</td>
                        <td className="p-2">{athlete.surname}</td>
                        <td className="p-2 text-center">
                          {judged ? (
                            <Badge className="bg-orange-500">Giudicato</Badge>
                          ) : (
                            <Badge variant="outline">Da giudicare</Badge>
                          )}
                        </td>
                        <td className="p-2 text-center text-sm">
                          {judgment ? (
                            <span className="font-mono">
                              T:{judgment.tecnica} C:{judgment.coreografia} I:{judgment.immagine} = <strong>{judgment.total}</strong>
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Main Competition Analysis component
const CompetitionAnalysis = ({ user, activeRole }) => {
  const [myAnalyses, setMyAnalyses] = useState([]);
  const [toJudgeAnalyses, setToJudgeAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list, judge, view
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editAthleteModal, setEditAthleteModal] = useState(null);
  const [editScoresModal, setEditScoresModal] = useState(null);

  const isJudgeOrSpeaker = user?.roles?.some(r => ['teacher', 'formatore', 'admin'].includes(r)) ||
                          ['teacher', 'formatore', 'admin'].includes(user?.role);
  
  const isAthlete = user?.roles?.some(r => r === 'atleta') || user?.role === 'atleta';
  const isAdmin = activeRole === 'admin' || user?.roles?.includes('admin');

  useEffect(() => {
    if (isJudgeOrSpeaker) {
      fetchData();
    }
  }, [isJudgeOrSpeaker]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch analyses created by user
      const myResponse = await axios.get(`${API}/competition-analysis/my-analyses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyAnalyses(myResponse.data);
      
      // Fetch analyses to judge
      const toJudgeResponse = await axios.get(`${API}/competition-analysis/to-judge`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setToJudgeAnalyses(toJudgeResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenJudge = (analysis) => {
    setSelectedAnalysis(analysis);
    setViewMode('judge');
  };

  const handleViewResults = (analysis) => {
    setSelectedAnalysis(analysis);
    setViewMode('view');
  };

  const handleBack = () => {
    setSelectedAnalysis(null);
    setViewMode('list');
    fetchData();
  };

  const handleDeleteAnalysis = async (analysisId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/competition-analysis/${analysisId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Analysis eliminata con successo');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting analysis:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleUpdateAthleteName = async () => {
    if (!editAthleteModal) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/competition-analysis/${editAthleteModal.analysisId}/athlete/${editAthleteModal.competitionNumber}?name=${encodeURIComponent(editAthleteModal.name)}&surname=${encodeURIComponent(editAthleteModal.surname)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Nome atleta aggiornato');
      setEditAthleteModal(null);
      fetchData();
      if (selectedAnalysis) {
        // Refresh selected analysis
        const updatedAnalysis = { ...selectedAnalysis };
        updatedAnalysis.athletes = updatedAnalysis.athletes.map(a => 
          a.competition_number === editAthleteModal.competitionNumber 
            ? { ...a, name: editAthleteModal.name, surname: editAthleteModal.surname }
            : a
        );
        setSelectedAnalysis(updatedAnalysis);
      }
    } catch (error) {
      console.error('Error updating athlete name:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  // If user is only athlete (not also judge/speaker), show their judgments
  if (isAthlete && !isJudgeOrSpeaker) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Award className="w-5 h-5 mr-2" />
            I Miei Giudizi di Gara
          </CardTitle>
          <CardDescription>
            Risultati delle tue partecipazioni alle competizioni
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AthleteCompetitionJudgments />
        </CardContent>
      </Card>
    );
  }

  if (!isJudgeOrSpeaker) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  // Judge view - submitting scores
  if (viewMode === 'judge' && selectedAnalysis) {
    return (
      <JudgeView 
        analysis={selectedAnalysis} 
        onBack={handleBack}
        onScoreSubmitted={fetchData}
      />
    );
  }

  // View results mode (for creator)
  if (viewMode === 'view' && selectedAnalysis) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <h2 className="text-xl font-bold">
            Risultati: {selectedAnalysis.event_title}
          </h2>
          {selectedAnalysis.event_code && (
            <Badge variant="secondary">{selectedAnalysis.event_code}</Badge>
          )}
          {selectedAnalysis.status === 'published' && (
            <Badge className="bg-green-500">Pubblicato</Badge>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Informazioni Analysis</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p><strong>Creata da:</strong> {selectedAnalysis.created_by_name}</p>
            <p><strong>Giudici:</strong> {selectedAnalysis.judge_names?.join(', ')}</p>
            <p><strong>Atleti:</strong> {selectedAnalysis.athletes?.length || 0}</p>
            <p><strong>Stato:</strong> {selectedAnalysis.status === 'published' ? 'Pubblicato' : 'In corso'}</p>
          </CardContent>
        </Card>

        <JudgmentsByAthlete 
          analysisId={selectedAnalysis.id} 
          analysisStatus={selectedAnalysis.status}
          onPublish={handleBack}
        />
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      {/* Header with create button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center">
          <Award className="w-6 h-6 mr-2" />
          Analysis di Gara
        </h2>
        <Button onClick={() => setShowCreate(true)} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" />
          Crea Analysis di Gara
        </Button>
      </div>

      {/* Analyses to judge */}
      {toJudgeAnalyses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileSpreadsheet className="w-5 h-5 mr-2" />
              Da Giudicare
            </CardTitle>
            <CardDescription>
              Analysis di Gara in cui sei stato selezionato come giudice
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {toJudgeAnalyses.map((analysis) => (
                <div 
                  key={analysis.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 flex items-center justify-between"
                >
                  <div>
                    <h3 className="font-semibold">{analysis.event_title}</h3>
                    {analysis.event_code && (
                      <Badge variant="outline" className="mr-2">{analysis.event_code}</Badge>
                    )}
                    <p className="text-sm text-gray-500">
                      Creata da: {analysis.created_by_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {analysis.athletes?.length || 0} atleti
                    </p>
                  </div>
                  <Button 
                    onClick={() => handleOpenJudge(analysis)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Giudica
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* My analyses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Le Mie Analysis di Gara
          </CardTitle>
          <CardDescription>
            Analysis di Gara che hai creato
          </CardDescription>
        </CardHeader>
        <CardContent>
          {myAnalyses.length === 0 ? (
            <p className="text-center py-8 text-gray-500">
              Non hai ancora creato nessuna Analysis di Gara
            </p>
          ) : (
            <div className="space-y-3">
              {myAnalyses.map((analysis) => (
                <div 
                  key={analysis.id}
                  className="p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold">{analysis.event_title}</h3>
                        {analysis.event_code && (
                          <Badge variant="outline">{analysis.event_code}</Badge>
                        )}
                        {analysis.status === 'published' && (
                          <Badge className="bg-green-500">Pubblicato</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        Giudici: {analysis.judge_names?.join(', ')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {analysis.athletes?.length || 0} atleti | 
                        Creata: {new Date(analysis.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button 
                        onClick={() => handleViewResults(analysis)}
                        variant="outline"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Visualizza
                      </Button>
                      {isAdmin && (
                        <Button 
                          onClick={() => setDeleteConfirm(analysis)}
                          variant="destructive"
                          size="icon"
                          data-testid={`delete-analysis-${analysis.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Admin: Athletes list for editing */}
                  {isAdmin && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs font-semibold text-gray-500 mb-2">
                        <Edit className="w-3 h-3 inline mr-1" />
                        Modifica Atleti (Admin)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {analysis.athletes?.slice(0, 5).map((athlete) => (
                          <button
                            key={athlete.competition_number}
                            onClick={() => setEditAthleteModal({
                              analysisId: analysis.id,
                              competitionNumber: athlete.competition_number,
                              name: athlete.name,
                              surname: athlete.surname
                            })}
                            className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded flex items-center"
                          >
                            <span className="font-mono mr-1">#{athlete.competition_number}</span>
                            {athlete.name} {athlete.surname}
                            <Edit className="w-3 h-3 ml-1 text-blue-500" />
                          </button>
                        ))}
                        {analysis.athletes?.length > 5 && (
                          <span className="text-xs text-gray-400 py-1">
                            +{analysis.athletes.length - 5} altri
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4 text-red-600">Conferma Eliminazione</h3>
            <p className="text-gray-600 mb-2">
              Sei sicuro di voler eliminare l&apos;Analysis di Gara:
            </p>
            <p className="font-semibold mb-4">&quot;{deleteConfirm.event_title}&quot;?</p>
            <p className="text-sm text-red-500 mb-6">
              ⚠️ Questa azione eliminerà anche tutti i giudizi associati e non può essere annullata.
            </p>
            <div className="flex space-x-4">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => handleDeleteAnalysis(deleteConfirm.id)}
                variant="destructive"
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Athlete Name Modal */}
      {editAthleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Modifica Nome Atleta</h3>
            <p className="text-gray-600 mb-4">
              Atleta N° <span className="font-mono font-bold">{editAthleteModal.competitionNumber}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Nome</Label>
                <Input
                  id="edit-name"
                  value={editAthleteModal.name}
                  onChange={(e) => setEditAthleteModal({...editAthleteModal, name: e.target.value})}
                  placeholder="Nome"
                />
              </div>
              <div>
                <Label htmlFor="edit-surname">Cognome</Label>
                <Input
                  id="edit-surname"
                  value={editAthleteModal.surname}
                  onChange={(e) => setEditAthleteModal({...editAthleteModal, surname: e.target.value})}
                  placeholder="Cognome"
                />
              </div>
            </div>
            
            <div className="flex space-x-4 mt-6">
              <Button
                variant="outline"
                onClick={() => setEditAthleteModal(null)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={handleUpdateAthleteName}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateCompetitionAnalysis 
          onClose={() => setShowCreate(false)}
          onCreated={fetchData}
        />
      )}
    </div>
  );
};

export default CompetitionAnalysis;
